import { useState, useMemo } from "react";
import { Button, ButtonGroup, Modal } from "@chaser/components";
import { Search } from "lucide-react";
import { getRoles } from "@/components/FeatureGate/RoleGate";
import { roleNames, AVAILABLE_ROLES, isGovernanceRole, hasGovernanceRole } from "@/util/constants";
import useShortCode from "@/hooks/useShortCode";
import useUserInfo from "@/hooks/useUserInfo";
import useCurrentUserRoles from "@/hooks/useCurrentUserRoles";
import usePush from "@/hooks/usePush";
import AuthenticationService from "@/services/authentication-service";
import RoleUsersModalUserRow from "./RoleUsersModalUserRow";
import styles from "./RoleUsersModal.module.scss";

function normalizeUsers(users, shortCode) {
  const { chaserUsers = [], invitedChaserUsers = [] } = users;

  const verified = chaserUsers.map((user) => {
    const { organisationRoles = [] } = getRoles(user, shortCode);
    const currentRoles = organisationRoles.filter((role) =>
      AVAILABLE_ROLES.includes(role),
    );
    return {
      key: user._id,
      userId: user._id,
      name: `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim(),
      email: user.emails?.[0]?.address || "",
      verified: true,
      currentRoles,
    };
  });

  const invited = invitedChaserUsers.map((user) => {
    const currentRoles = (user.roles || []).filter((role) =>
      AVAILABLE_ROLES.includes(role),
    );
    return {
      key: user.email,
      email: user.email,
      name: "",
      verified: false,
      currentRoles,
    };
  });

  return verified.concat(invited);
}

function matchesSearch(user, query) {
  const q = query.toLowerCase();
  return (
    user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
  );
}

const RoleUsersModal = ({
  open,
  close,
  onClosed,
  role,
  initialMode,
  users,
  applyOptimisticUpdate,
  manageDisabled,
}) => {
  const shortCode = useShortCode();
  const currentUser = useUserInfo();
  const { isStaff, isOwner, isManager } = useCurrentUserRoles();
  const [mode, setMode] = useState(initialMode || "current");
  const [search, setSearch] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});
  const [isApplying, setIsApplying] = useState(false);

  const { execute: setRolePermission } = usePush(
    AuthenticationService.setRolePermission,
    {
      errorToast: true,
    },
  );
  const { execute: setInvitedUserRole } = usePush(
    AuthenticationService.setInvitedUserRole,
    {
      errorToast: true,
    },
  );

  const allUsers = useMemo(
    () => normalizeUsers(users, shortCode),
    [users, shortCode],
  );

  const roleIsGovernance = isGovernanceRole(role);

  const currentUsers = useMemo(
    () => allUsers.filter((u) => u.currentRoles.includes(role)),
    [allUsers, role],
  );

  const filteredCurrentUsers = useMemo(
    () =>
      search
        ? currentUsers.filter((u) => matchesSearch(u, search))
        : currentUsers,
    [currentUsers, search],
  );

  const filteredAllUsers = useMemo(
    () =>
      search ? allUsers.filter((u) => matchesSearch(u, search)) : allUsers,
    [allUsers, search],
  );

  function isChecked(user) {
    if (pendingChanges[user.key] !== undefined) return pendingChanges[user.key];
    return user.currentRoles.includes(role);
  }

  function getDisabledState(user) {
    const isSelf = user.verified && user.userId === currentUser._id;
    if (isSelf)
      return { disabled: true, reason: "Cannot modify your own roles" };

    if (!isStaff && !isOwner) {
      if (isManager) {
        if (roleIsGovernance) {
          return {
            disabled: true,
            reason: "Cannot assign a governance role",
          };
        }
        if (hasGovernanceRole(user.currentRoles)) {
          return {
            disabled: true,
            reason: "Cannot modify a user with a governance role",
          };
        }
      } else {
        return { disabled: true, reason: "Insufficient permissions" };
      }
    }

    const wouldHaveRole = isChecked(user);
    if (
      wouldHaveRole &&
      user.currentRoles.length === 1 &&
      user.currentRoles[0] === role
    ) {
      if (
        pendingChanges[user.key] === undefined ||
        pendingChanges[user.key] === true
      ) {
        return { disabled: true, reason: "A user must have at least one role" };
      }
    }

    if (!wouldHaveRole) {
      const rolesAfterRemove = user.currentRoles.filter((r) => r !== role);
      const addedRoles = Object.entries(pendingChanges).filter(
        ([k, v]) => k === user.key && v,
      ).length;
      if (rolesAfterRemove.length === 0 && addedRoles === 0) {
        return { disabled: true, reason: "A user must have at least one role" };
      }
    }

    return { disabled: false, reason: null };
  }

  function handleToggle(userKey) {
    setPendingChanges((prev) => {
      const user = allUsers.find((u) => u.key === userKey);
      const currentlyHasRole = user.currentRoles.includes(role);
      const pendingValue = prev[userKey];

      if (pendingValue !== undefined) {
        const next = { ...prev };
        delete next[userKey];
        return next;
      }

      return { ...prev, [userKey]: !currentlyHasRole };
    });
  }

  function handleModeSwitch(newMode) {
    if (newMode === mode) return;
    setPendingChanges({});
    setMode(newMode);
  }

  const hasChanges = Object.keys(pendingChanges).length > 0;

  async function handleApply() {
    const changedUsers = Object.entries(pendingChanges)
      .map(([key, shouldHaveRole]) => {
        const user = allUsers.find((u) => u.key === key);
        if (!user) return null;
        const newRoles = shouldHaveRole
          ? [...user.currentRoles, role]
          : user.currentRoles.filter((r) => r !== role);
        return { ...user, newRoles };
      })
      .filter(Boolean);

    if (changedUsers.length === 0) return;

    setIsApplying(true);

    const minWait = new Promise((res) => setTimeout(res, 1000));

    for (const changedUser of changedUsers) {
      const apiCall = changedUser.verified
        ? setRolePermission(
            { roles: changedUser.newRoles, userId: changedUser.userId },
            shortCode,
          )
        : setInvitedUserRole(
            { roles: changedUser.newRoles, email: changedUser.email },
            shortCode,
          );

      await applyOptimisticUpdate((state) => {
        if (changedUser.verified) {
          const target = state.chaserUsers?.find(
            (u) => u._id === changedUser.userId,
          );
          if (!target) return;
          if (!target.permissions) target.permissions = {};
          if (!target.permissions.organisation)
            target.permissions.organisation = [];
          let orgPerm = target.permissions.organisation.find(
            (p) => p.orgShortCode === shortCode,
          );
          if (!orgPerm) {
            orgPerm = { orgShortCode: shortCode, roles: changedUser.newRoles };
            target.permissions.organisation.push(orgPerm);
          } else {
            orgPerm.roles = changedUser.newRoles;
          }
        } else {
          const target = state.invitedChaserUsers?.find(
            (u) => u.email === changedUser.email,
          );
          if (!target) return;
          target.roles = changedUser.newRoles;
        }
      }, apiCall);
    }

    await minWait;
    setIsApplying(false);
    setPendingChanges({});
    close();
  }

  const title = `${roleNames[role]} — Users`;

  return (
    <Modal
      open={open}
      toggle={close}
      onClosed={onClosed}
      title={title}
      size="medium"
      body={
        <div className={styles.modalBody}>
          <div className={styles.searchWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${mode === "current" ? styles.tabActive : ""}`}
              onClick={() => handleModeSwitch("current")}
            >
              Current users
            </button>
            <button
              type="button"
              className={`${styles.tab} ${mode === "manage" ? styles.tabActive : ""}`}
              onClick={() => handleModeSwitch("manage")}
              disabled={manageDisabled}
            >
              Add / remove
            </button>
          </div>

          <div className={styles.userList}>
            {mode === "current" &&
              (filteredCurrentUsers.length === 0 ? (
                <div className={styles.emptyState}>
                  {search ? "No matching users" : "No users have this role"}
                </div>
              ) : (
                filteredCurrentUsers.map((user) => (
                  <RoleUsersModalUserRow
                    key={user.key}
                    user={user}
                    showCheckbox={false}
                  />
                ))
              ))}

            {mode === "manage" &&
              (filteredAllUsers.length === 0 ? (
                <div className={styles.emptyState}>No matching users</div>
              ) : (
                filteredAllUsers.map((user) => {
                  const { disabled, reason } = getDisabledState(user);
                  return (
                    <RoleUsersModalUserRow
                      key={user.key}
                      user={user}
                      showCheckbox
                      checked={isChecked(user)}
                      disabled={disabled}
                      disabledReason={reason}
                      onToggle={handleToggle}
                    />
                  );
                })
              ))}
          </div>
        </div>
      }
      footer={
        mode === "manage" ? (
          <ButtonGroup>
            <Button appearance="secondary" onClick={close}>
              Cancel
            </Button>
            <Button disabled={!hasChanges || isApplying} onClick={handleApply}>
              {isApplying ? "Applying..." : "Apply"}
            </Button>
          </ButtonGroup>
        ) : null
      }
    />
  );
};

export default RoleUsersModal;
